import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { fetchData } from "../utils/api.js";

import "./common/SharedTable.css";

import ConfirmationModal from "./common/ConfirmationModal.js";
import { Drawer } from "./common/Drawer.js";

import { formatPhoneNumber, PersonList } from "./common/PersonList.js";
import { Button } from "./ui/Button";

const TeamFans = ({ team, isCoach, user }) => {
  const [fans, setFans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [fanToRemove, setFanToRemove] = useState(null);
  const [editingFan, setEditingFan] = useState(null);
  const [newFan, setNewFan] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFans = async () => {
      setLoading(true);
      try {
        const data = await fetchData(
          `/roster_details?team_id=eq.${team.id || team.team_id}&role=eq.fan`,
        );
        if (data) {
          setFans(data.map((f) => ({ ...f, id: f.person_id })));
        }
      } catch (err) {
        console.error("Error fetching fans:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFans();
  }, [team.id, team.team_id]);

  const handleSaveFan = async (e) => {
    e.preventDefault();
    setError("");

    if (!newFan.first_name.trim() || !newFan.last_name.trim()) {
      setError("First Name and Last Name are required.");
      return;
    }
    const isValidEmail = (email) => {
      const str = String(email || "").trim();
      const atIdx = str.indexOf("@");
      const dotIdx = str.lastIndexOf(".");
      return (
        !str.includes(" ") &&
        atIdx > 0 &&
        dotIdx > atIdx + 1 &&
        dotIdx < str.length - 1
      );
    };
    if (newFan.email && !isValidEmail(newFan.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    const payload = {
      first_name: newFan.first_name,
      last_name: newFan.last_name,
      email: newFan.email || null,
      phone: newFan.phone_number || newFan.phone || null,
    };

    try {
      if (editingFan) {
        const result = await fetchData(
          `/people?person_id=eq.${editingFan.person_id}`,
          {
            method: "PATCH",
            headers: { Prefer: "return=representation" },
            body: JSON.stringify(payload),
          },
        );
        if (result && result.length > 0) {
          const updated = {
            ...result[0],
            id: result[0].person_id,
            role: "fan",
            team_id: team.id || team.team_id,
          };
          setFans(
            fans.map((f) => (f.person_id === updated.person_id ? updated : f)),
          );
        }
      } else {
        const result = await fetchData(`/people`, {
          method: "POST",
          headers: { Prefer: "return=representation" },
          body: JSON.stringify(payload),
        });
        if (result && result.length > 0) {
          const newPlayer = result[0];
          await fetchData(`/team_rosters`, {
            method: "POST",
            body: JSON.stringify({
              team_id: team.id || team.team_id,
              person_id: newPlayer.person_id,
              role: "fan",
            }),
          });
          setFans([
            ...fans,
            {
              ...newPlayer,
              id: newPlayer.person_id,
              role: "fan",
              team_id: team.id || team.team_id,
            },
          ]);
        }
      }

      setShowAddModal(false);
      setEditingFan(null);
      setNewFan({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
      });
    } catch (err) {
      console.error("Error saving fan:", err);
      setError("Failed to save fan. Please try again.");
    }
  };

  const openAddModal = () => {
    setEditingFan(null);
    setNewFan({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setShowAddModal(true);
  };

  const openEditModal = (fan) => {
    setEditingFan(fan);
    setNewFan({
      first_name: fan.first_name || "",
      last_name: fan.last_name || "",
      email: fan.email || "",
      phone_number: formatPhoneNumber(fan.phone_number || ""),
    });
    setShowAddModal(true);
  };

  const confirmRemoveFan = (fan) => {
    setFanToRemove(fan);
  };

  const cancelRemoveFan = () => {
    setFanToRemove(null);
  };

  const executeRemoveFan = async () => {
    if (!fanToRemove) return;
    const fanId = fanToRemove.id;
    try {
      await fetchData(
        `/team_rosters?team_id=eq.${team.id || team.team_id}&person_id=eq.${fanId}`,
        {
          method: "DELETE",
        },
      );
      setFans(fans.filter((f) => f.person_id !== fanId));
      setFanToRemove(null);
    } catch (err) {
      console.error("Error removing fan:", err);
      setError("Network error");
      setFanToRemove(null);
    }
  };

  let content;
  if (loading) {
    content = <div className="loading-spinner" />;
  } else {
    content = (
      <PersonList
        people={fans}
        type="Member"
        onEdit={openEditModal}
        onRemove={confirmRemoveFan}
        canEdit={isCoach}
        showContactColumn={Boolean(isCoach)}
        emptyMessage="No fans added yet."
      />
    );
  }

  return (
    <div>
      {isCoach && (
        <div
          className="admin-row-header"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "0.75rem",
          }}
        >
          <Button variant="primary" type="button" onClick={openAddModal}>
            Add Fan
          </Button>
        </div>
      )}

      {error && !showAddModal && <div className="error-banner">{error}</div>}

      {content}

      <Drawer
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={editingFan ? "Edit Fan" : "Add New Fan"}
      >
        {error && <p className="error-text">{error}</p>}
        <form onSubmit={handleSaveFan} className="form-compact">
          <div className="form-group">
            <label htmlFor="fan-first-name">First Name</label>
            <input
              id="fan-first-name"
              type="text"
              value={newFan.first_name}
              onChange={(e) =>
                setNewFan({ ...newFan, first_name: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="fan-last-name">Last Name</label>
            <input
              id="fan-last-name"
              type="text"
              value={newFan.last_name}
              onChange={(e) =>
                setNewFan({ ...newFan, last_name: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="fan-email">Email (Optional)</label>
            <input
              id="fan-email"
              type="email"
              value={newFan.email}
              onChange={(e) => setNewFan({ ...newFan, email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fan-phone">Phone (Optional)</label>
            <input
              id="fan-phone"
              type="tel"
              value={newFan.phone_number}
              onChange={(e) =>
                setNewFan({
                  ...newFan,
                  phone_number: formatPhoneNumber(e.target.value),
                })
              }
              placeholder="e.g. (555) 555-5555"
            />
          </div>
          <div className="modal-actions" style={{ marginTop: "1rem" }}>
            <Button
              variant="outline"
              type="button"

              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingFan ? "Save Changes" : "Add Fan"}
            </Button>
          </div>
        </form>
      </Drawer>

      {fanToRemove && (
        <ConfirmationModal
          isOpen={!!fanToRemove}
          onClose={cancelRemoveFan}
          onConfirm={executeRemoveFan}
          title="Remove Fan?"
        >
          Are you sure you want to remove{" "}
          <strong>
            {fanToRemove.first_name} {fanToRemove.last_name}
          </strong>{" "}
          ?
        </ConfirmationModal>
      )}
    </div>
  );
};

TeamFans.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    team_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  isCoach: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string,
    firebaseUid: PropTypes.string,
    getIdToken: PropTypes.func,
  }),
};

export default TeamFans;
